/**
 * The countdown a deploy announces (`server_restart`), and the wait for the
 * server to come back before the page reloads into the game.
 */

/** Calls `onTick` with the seconds left, now then once a second down to 1; returns the stop. */
export function countDown(seconds: number, onTick: (left: number) => void): () => void {
  let left = seconds;
  onTick(left);
  const timer = window.setInterval(() => {
    left -= 1;
    if (left < 1) {
      window.clearInterval(timer);
      return;
    }
    onTick(left);
  }, 1000);
  return () => {
    window.clearInterval(timer);
  };
}

/** How often the health route is asked while the server is away. */
const PROBE_EVERY_MS = 2000;
/** `uptime` is rounded to the second, and the old process may close a little after its rooms. */
const UPTIME_SLACK_MS = 2000;

interface HealthBody {
  status?: unknown;
  uptime?: unknown;
}

/**
 * Whether this answer comes from a server worth reloading for. After a
 * restart, the process that closed the session can still answer for a
 * moment: only one started since the close counts.
 */
export function serverIsBack(body: HealthBody, closedAt: number | undefined, now: number): boolean {
  if (body.status !== 'ok') return false;
  if (closedAt === undefined) return true;
  if (typeof body.uptime !== 'number') return false;
  return now - body.uptime * 1000 >= closedAt - UPTIME_SLACK_MS;
}

/** Resolves once `/health` answers from a server that is back (see `serverIsBack`). */
export async function waitForServer(healthUrl: string, closedAt?: number): Promise<void> {
  for (;;) {
    await new Promise((resolve) => window.setTimeout(resolve, PROBE_EVERY_MS));
    try {
      const response = await fetch(healthUrl, { cache: 'no-store' });
      if (
        response.ok &&
        serverIsBack((await response.json()) as HealthBody, closedAt, Date.now())
      ) {
        return;
      }
    } catch {
      // Down, or Caddy answering for a container not there yet: ask again.
    }
  }
}
